import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Row, Tabs, theme } from 'antd';
import React, { CSSProperties, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/core/stores/data-store';
import { container } from 'src/inversify.config';
import { getValidNotificationTypes, NotificationDataStore, NotificationDataSummary } from 'src/stores/notifications-store';
import { useSessionNotifications } from './notifications-session-store';
import { NotificationCard } from './notifications-card';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment, { Moment } from 'moment';
import { capitalize } from 'lodash';
import { Bell, Trash2, Glasses, SquareArrowOutUpRight } from 'lucide-react';
import { NotificationsEmpty } from './notifications-empty';
import { CustomGlobalThemeToken } from 'src/common/themes';
import useScreenSize from 'src/core/ui/hooks/use-screen-size';
import { useNavigate } from 'react-router';
const { useToken } = theme;

export const Notifications = ({ updateVisibility, revision }: { updateVisibility: Dispatch<SetStateAction<boolean>>; revision: number }) => {
  const notificationsStore = container.get(NotificationDataStore);
  const sessionNotifications = useSessionNotifications();
  const [activeTab, setActiveTab] = useState('unread');
  const [removingNotifications, setRemovingNotifications] = useState(false);
  const { token } = useToken();
  const customToken = token as CustomGlobalThemeToken;
  const screenSize = useScreenSize();
  const onMobile = screenSize.width < 600;
  const navigate = useNavigate();

  const notificationTypesFilter = `(${getValidNotificationTypes()
    .map((v) => `type eq '${v}'`)
    .join(' or ')})`;

  const readNotificationsQuery = useRef<Partial<Query>>({
    filter: [{ isRead: true }, notificationTypesFilter],
    take: 10,
    skip: 0,
    orderBy: [{ field: 'createdAt', direction: 'Descending', useProfile: false }],
  });

  const unreadNotificationsQuery = useRef<Partial<Query>>({
    filter: [{ isRead: false }, notificationTypesFilter],
    take: 10,
    skip: 0,
    orderBy: [{ field: 'createdAt', direction: 'Descending', useProfile: false }],
  });

  const { t, i18n } = useTranslation();

  useEffect(() => {
    loadNextUnreadBatch(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveTab('unread');
  }, [revision]);

  const updateReadNotifications = () => {
    readNotificationsQuery.current.skip = 0;
    notificationsStore.getNotifications(readNotificationsQuery.current).then((result) => {
      sessionNotifications.setReadNotifications(result?.items ?? [], result?.count ?? 0);
    });
  };

  const loadNextUnreadBatch = async (first?: boolean) => {
    if (sessionNotifications.state.unreadNotifications.count === 0 && !first) return;
    const loadedNotifications = await notificationsStore.getNotifications({
      ...unreadNotificationsQuery.current,
      skip: sessionNotifications.state.unreadNotifications.items.length,
    });
    if (loadedNotifications?.items.length === 0) return;

    unreadNotificationsQuery.current = {
      ...unreadNotificationsQuery.current,
      skip: sessionNotifications.state.unreadNotifications.items.length,
    };
    const newUnreadNotificationsData = {
      items: [...sessionNotifications.state.unreadNotifications.items, ...(loadedNotifications?.items ?? [])],
      count: first ? loadedNotifications?.count ?? 0 : sessionNotifications.state.unreadNotifications.count,
    };
    sessionNotifications.setUnreadNotifications(newUnreadNotificationsData.items, newUnreadNotificationsData.count);
  };

  const loadNextReadBatch = async () => {
    const loadedNotifications = await notificationsStore.getNotifications({
      ...readNotificationsQuery.current,
      skip: sessionNotifications.state.readNotifications.items.length,
    });
    if (loadedNotifications?.items.length === 0) return;
    readNotificationsQuery.current = {
      ...readNotificationsQuery.current,
      skip: sessionNotifications.state.readNotifications.items.length,
    };
    const newReadNotificationsData = {
      items: [...sessionNotifications.state.readNotifications.items, ...(loadedNotifications?.items ?? [])],
      count: sessionNotifications.state.readNotifications.count,
    };
    sessionNotifications.setReadNotifications(newReadNotificationsData.items, newReadNotificationsData.count);
  };

  const getDateDivision = (notificationList: NotificationDataSummary[], index: number) => {
    const dividerStyle: CSSProperties = { borderColor: customToken.colorText, borderWidth: '2px' };

    const now = moment();
    const currentNotificationMoment = moment(notificationList[index].createdAt);
    const currentGroup = getGroup(currentNotificationMoment, now);

    if (index === 0) {
      return <Divider style={dividerStyle}>{currentGroup.label}</Divider>;
    }

    const previousNotificationMoment = moment(notificationList[index - 1].createdAt);
    const previousGroup = getGroup(previousNotificationMoment, now);

    if (currentGroup.key === previousGroup.key) {
      return <></>;
    }

    return <Divider style={dividerStyle}>{currentGroup.label}</Divider>;
  };

  const getGroup = (date: Moment, now: Moment) => {
    if (now.diff(date, 'minutes') < 60) {
      return { key: 'now', label: t('Now') };
    }

    if (date.isSame(now, 'day')) {
      return { key: 'today', label: t('Today') };
    }

    if (date.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return { key: 'yesterday', label: t('Yesterday') };
    }

    if (date.isSame(now, 'week')) {
      return { key: 'this-week', label: t('ThisWeek') };
    }

    if (date.isSame(now.clone().subtract(1, 'week'), 'week')) {
      return { key: 'last-week', label: t('LastWeek') };
    }

    if (date.isSame(now, 'month')) {
      return { key: 'this-month', label: t('ThisMonth') };
    }

    if (date.year() !== now.year()) {
      return {
        key: date.format('YYYY-MM'),
        label: capitalize(date.locale(i18n.language).format('MMMM, YYYY')),
      };
    }

    return {
      key: date.format('MM'),
      label: capitalize(date.locale(i18n.language).format('MMMM')),
    };
  };

  return (
    <div style={{ minWidth: 280, width: onMobile ? undefined : 500, backgroundColor: customToken.genericContainerBg }}>
      <Row style={{ marginTop: 10, marginLeft: 10, marginRight: 10 }} justify={'space-between'}>
        <Col>
          <h5 style={{ margin: 0 }}>
            <Bell size={18} style={{ marginRight: 8 }} />
            {t('Notifications')}
            <span
              style={{ marginLeft: 8 }}
              className="clickable-span strong"
              onClick={() => {
                navigate('/notifications');
                updateVisibility(false);
              }}
            >
              <SquareArrowOutUpRight size={20} />
            </span>
          </h5>
        </Col>
        <Col>
          <span
            style={{ cursor: 'pointer', color: customToken.actionIconColor }}
            onClick={() => {
              updateVisibility(false);
            }}
          >
            <CloseOutlined />
          </span>
        </Col>
      </Row>
      <Tabs
        tabBarStyle={{ borderBottom: `2px solid ${customToken.tabSolidBorderColor}` }}
        style={{ margin: '15px 10px 15px 10px' }}
        activeKey={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'read') {
            updateReadNotifications();
          }
        }}
        items={[
          {
            key: 'unread',
            label: t('Unread'),
            children: (
              <>
                <InfiniteScroll
                  style={{ scrollbarWidth: 'thin', maxHeight: 450, minHeight: 20 }}
                  dataLength={sessionNotifications.state.unreadNotifications.items.length}
                  next={loadNextUnreadBatch}
                  hasMore={sessionNotifications.state.unreadNotifications.count !== sessionNotifications.state.unreadNotifications.items.length}
                  loader={
                    <div style={{ display: 'flex' }}>
                      <LoadingOutlined style={{ margin: 'auto' }} spin={true} />
                    </div>
                  }
                  endMessage={sessionNotifications.state.unreadNotifications.count === 0 ? <NotificationsEmpty /> : <></>}
                >
                  {sessionNotifications.state.unreadNotifications.items.map((v, i) => (
                    <React.Fragment key={v.id}>
                      <NotificationCard
                        mode="unread"
                        onClick={() => {
                          updateVisibility(false);
                        }}
                        notification={v}
                        onRemove={(id: string) => {
                          sessionNotifications.setUnreadNotifications(
                            sessionNotifications.state.unreadNotifications.items.filter((v) => v.id !== id),
                            sessionNotifications.state.unreadNotifications.count - 1
                          );
                        }}
                      />
                    </React.Fragment>
                  ))}
                </InfiniteScroll>
                {sessionNotifications.state.unreadNotifications.count > 0 && (
                  <>
                    <Divider style={{ borderColor: customToken.tabSolidBorderColor }} />
                    <Row justify={'space-evenly'} gutter={[10, 10]}>
                      <Col>
                        <Button
                          disabled={sessionNotifications.state.unreadNotifications.count === 0}
                          onClick={async () => {
                            sessionNotifications.readAll();
                            notificationsStore.readAll();
                          }}
                          type="default"
                          style={{
                            minWidth: 200,
                            maxHeight: 40,
                            margin: 'auto',
                            borderRadius: 2,
                          }}
                        >
                          {<Glasses />}
                          {t('MarkAsReadAll')}
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          loading={removingNotifications}
                          disabled={sessionNotifications.state.unreadNotifications.count === 0}
                          onClick={async () => {
                            setRemovingNotifications(true);
                            const response = await notificationsStore.deleteAllUnread();
                            if (response) {
                              sessionNotifications.setUnreadNotifications([], 0);
                            }
                            setRemovingNotifications(false);
                          }}
                          type="primary"
                          style={{
                            backgroundColor: customToken.colorPrimary,
                            maxHeight: 40,
                            margin: 'auto',
                            borderRadius: 2,
                            minWidth: 200,
                          }}
                        >
                          {!removingNotifications && <Trash2 />}
                          {t('DeleteAllNotifications')}
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            ),
          },
          {
            key: 'read',
            label: t('Read'),
            destroyOnHidden: true,
            children: (
              <>
                <InfiniteScroll
                  style={{ scrollbarWidth: 'thin', maxHeight: 450, minHeight: 20 }}
                  dataLength={sessionNotifications.state.readNotifications.items.length}
                  next={loadNextReadBatch}
                  hasMore={sessionNotifications.state.readNotifications.count !== sessionNotifications.state.readNotifications.items.length}
                  loader={
                    <div style={{ display: 'flex' }}>
                      <LoadingOutlined style={{ margin: 'auto' }} spin={true} />
                    </div>
                  }
                  endMessage={sessionNotifications.state.readNotifications.count === 0 ? <NotificationsEmpty /> : <></>}
                >
                  {sessionNotifications.state.readNotifications.items.map((v, i) => (
                    <React.Fragment key={v.id}>
                      {getDateDivision(sessionNotifications.state.readNotifications.items, i)}
                      <NotificationCard
                        mode="read"
                        notification={v}
                        onClick={() => {
                          updateVisibility(false);
                        }}
                        onRemove={(id: string) => {
                          sessionNotifications.setReadNotifications(
                            sessionNotifications.state.readNotifications.items.filter((v) => v.id !== id),
                            sessionNotifications.state.readNotifications.count - 1
                          );
                        }}
                      />
                    </React.Fragment>
                  ))}
                </InfiniteScroll>
                {sessionNotifications.state.readNotifications.count > 0 && (
                  <>
                    <Divider style={{ borderColor: customToken.tabSolidBorderColor }} />
                    <div style={{ display: 'flex' }}>
                      <Button
                        loading={removingNotifications}
                        onClick={async () => {
                          setRemovingNotifications(true);
                          const response = await notificationsStore.deleteAllRead();
                          if (response) {
                            sessionNotifications.setReadNotifications([], 0);
                          }
                          setRemovingNotifications(false);
                        }}
                        disabled={sessionNotifications.state.readNotifications.count === 0}
                        type="primary"
                        style={{ backgroundColor: customToken.colorPrimary, maxHeight: 40, margin: 'auto', width: '80%', borderRadius: 2 }}
                      >
                        {!removingNotifications && <Trash2 />}
                        {t('DeleteAllNotifications')}
                      </Button>
                    </div>
                  </>
                )}
              </>
            ),
          },
        ]}
      ></Tabs>
    </div>
  );
};
